













































import { Component, Prop, Mixins } from "vue-property-decorator";
import { mapState } from "vuex";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import HasValidation from "@/mixins/HasValidation";
import Context from "@/mixins/context";

// Enums
import { DataModules } from "@/store/modules/data";

// Types
import { IState } from "@/store";
import {
  IBlueprintField,
  IProvisionFunctionFieldsForm,
  IProvisionConfigurationFunction,
  IProvisionRequest
} from "@/models/provisioning";
import { IContract, IContractProduct } from "@/models/contracts";

@Component({
  computed: {
    ...mapState({
      contractProductFunctionFieldsScope(state: IState, getters) {
        return getters[
          `data/${this.contractProductsDataModule}/contractProductFunctionFieldsScope`
        ];
      },
      provisioningFunctionFields(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: this.contractProductsDataModule,
          scope: this.contractProductFunctionFieldsScope
        });
      }
    })
  },
  components: {
    "provision-field": () =>
      import(
        "@/components/app/admin/catalogue/products/provisioning/provisionField.vue"
      )
  }
})
export default class ContractProductFunctionFieldsModal extends Mixins(
  RequestPromises,
  HasValidation,
  Context
) {
  // State
  public contractProductFunctionFieldsScope!: string;
  public provisioningFunctionFields!: IBlueprintField[];

  // Props
  @Prop({ required: true })
  public contractId!: IContract["id"];

  @Prop({ required: true })
  public productId!: IContractProduct["id"];

  @Prop({ required: true })
  public executedFunction!: IProvisionConfigurationFunction;

  // Data
  public form = { provision_field_values: {} } as IProvisionFunctionFieldsForm;
  public formClone = {
    provision_field_values: {}
  } as IProvisionFunctionFieldsForm;

  public contractProductsDataModule: DataModules =
    DataModules.PROVISIONING_CONTRACT_PRODUCTS;

  public isLoading = true;
  public isReloading = false;
  public requestError = false;
  public isProcessing = false;

  public created() {
    this.loadData();
  }

  public async loadData() {
    try {
      await this.getProvisioningFunctionFields(this.isReloading);
      this.initializeForm();
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    }
    this.isLoading = false;
    this.isReloading = false;
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public getProvisioningFunctionFields(ignoreStored = false) {
    return this.$store.dispatch(
      `data/${this.contractProductsDataModule}/listContractProductFunctionFields`,
      {
        contractId: this.contractId,
        productId: this.productId,
        functionId: this.executedFunction.id,
        scope: this.contractProductFunctionFieldsScope,
        vm: this,
        ignoreStored
      }
    );
  }

  public initializeForm() {
    this.$_.forEach(this.provisioningFunctionFields, field => {
      this.$set(
        this.form.provision_field_values,
        `${field.name}`,
        field.default_value
      );
    });

    this.formClone = this.$_.cloneDeep(this.form);
  }

  public get filteredProvisioningFunctionFields() {
    if (!this.isClient) {
      return this.provisioningFunctionFields;
    }

    return this.$_.filter(this.provisioningFunctionFields, [
      "customer_enabled",
      true
    ]);
  }

  public async validateBeforeSubmit() {
    try {
      if (!(await this.validate())) {
        throw this.$t("_sentence.form_is_not_valid");
      }

      this.isProcessing = true;
      const request = await this.executeFunction();
      const result = this.$_.first(request.results || []);
      const resultHasError = this.$_.get(result, "error_id");

      this.$buefy[resultHasError ? "snackbar" : "toast"].open({
        type: resultHasError ? "is-danger" : "is-dark",
        message: this.executedFunction.async
          ? (this.$t("_.operation_in_progress") as string)
          : (this.$_.get(result, "message") as string)
      });

      this.$emit("success", request);
      this.$emit("close");
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    }
    this.isProcessing = false;
  }

  public executeFunction(): Promise<IProvisionRequest> {
    return this.$store.dispatch(
      `data/${this.contractProductsDataModule}/executeProvisioningFunction`,
      {
        contractId: this.contractId,
        productId: this.productId,
        data: {
          blueprint_function_id: this.executedFunction.id,
          ...this.newValues
        }
      }
    );
  }

  public get newValues() {
    const newValues: any = this.$_.omitBy(
      this.form.provision_field_values,
      (v, k) => {
        return this.formClone.provision_field_values[k] === v;
      }
    );

    return { provision_field_values: newValues };
  }

  public get formIsDisabled() {
    return this.hasErrors || this.isLoading;
  }

  public get focusedFieldId() {
    return this.$_.get(
      this.$_.find(this.filteredProvisioningFunctionFields, field => {
        return (
          (this.isClient && field.customer_can_update) ||
          (this.isAdmin && field.staff_can_update)
        );
      }),
      "id"
    );
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: this.contractProductsDataModule,
      scope: this.contractProductFunctionFieldsScope,
      vm: this
    });
  }
}
